import React from "react";
import { Activity, Eye, Zap, Waves } from "lucide-react";
import { fmtDate, SeverityBadge, EmptyMini, CONTROL_COLOR } from "./shared";
import DiagnosticsSuggestion from "@/components/DiagnosticsSuggestion";

export default function OverviewTab({ nihss, gcs, migraine, seizure, onJump }) {
  const latestN = nihss.records[0];
  const latestG = gcs.records[0];
  const control = seizure.control;
  return (
    <div className="space-y-4" data-testid="neuro-overview">
      <DiagnosticsSuggestion specialty="neurology" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <button onClick={() => onJump("nihss")}
              data-testid="neuro-overview-nihss-card"
              className="text-left ros-card hover:border-[#A78BFA]/40 transition-colors">
        <div className="flex items-center gap-2 ros-label"><Activity size={12} /> Latest NIHSS</div>
        {latestN ? (
          <>
            <div className="mt-3 text-4xl font-heading font-bold text-white">
              {latestN.total}<span className="text-lg text-[#A3ACA7] ml-1">/ 42</span>
            </div>
            <SeverityBadge value={latestN.severity} />
            <div className="text-xs text-[#7A8480] mt-3">Assessed {fmtDate(latestN.date)}</div>
          </>
        ) : <EmptyMini text="No NIHSS assessments yet." />}
      </button>

      <button onClick={() => onJump("gcs")}
              data-testid="neuro-overview-gcs-card"
              className="text-left ros-card hover:border-[#A78BFA]/40 transition-colors">
        <div className="flex items-center gap-2 ros-label"><Eye size={12} /> Latest GCS</div>
        {latestG ? (
          <>
            <div className="mt-3 text-4xl font-heading font-bold text-white">
              {latestG.label}<span className="text-lg text-[#A3ACA7] ml-1">/ 15</span>
            </div>
            <SeverityBadge value={latestG.severity} />
            <div className="text-xs text-[#7A8480] mt-3">
              E{latestG.eye} V{latestG.verbal} M{latestG.motor} · {fmtDate(latestG.date)}
            </div>
          </>
        ) : <EmptyMini text="No GCS assessments yet." />}
      </button>

      <button onClick={() => onJump("migraine")}
              data-testid="neuro-overview-migraine-card"
              className="text-left ros-card hover:border-[#A78BFA]/40 transition-colors">
        <div className="flex items-center gap-2 ros-label"><Zap size={12} /> Migraine (30-day)</div>
        <div className="mt-3 flex items-baseline gap-6">
          <div>
            <div className="text-3xl font-heading font-bold text-white">{migraine.summary?.count_last_30d ?? 0}</div>
            <div className="text-[10px] text-[#A3ACA7]">episodes</div>
          </div>
          {migraine.summary?.avg_pain_last_30d != null && (
            <div>
              <div className="text-3xl font-heading font-bold text-white">
                {migraine.summary.avg_pain_last_30d}<span className="text-lg text-[#A3ACA7] ml-1">/ 10</span>
              </div>
              <div className="text-[10px] text-[#A3ACA7]">avg pain</div>
            </div>
          )}
        </div>
        <div className="text-xs text-[#7A8480] mt-3">
          Total logged: {migraine.episodes?.length ?? 0}
        </div>
      </button>

      <button onClick={() => onJump("seizure")}
              data-testid="neuro-overview-seizure-card"
              className="text-left ros-card hover:border-[#A78BFA]/40 transition-colors">
        <div className="flex items-center gap-2 ros-label"><Waves size={12} /> Seizure control</div>
        {control ? (
          <>
            <div className="mt-3 text-2xl font-heading font-bold" style={{ color: CONTROL_COLOR[control.band] || "#fff" }}>
              {control.band}
            </div>
            <div className="text-xs text-[#A3ACA7] mt-1">
              {seizure.summary?.count_last_30d} in last 30d · Days since last: {seizure.summary?.days_since_last ?? "—"}
            </div>
            <div className="text-xs text-[#D1D5DB] mt-3 leading-relaxed">{control.recommendation}</div>
          </>
        ) : <EmptyMini text="No seizure history yet." />}
      </button>
      </div>
    </div>
  );
}
